import { useState } from 'react';
import { KeyRound, Loader2 } from 'lucide-react';
import type { plans } from '@/lib/client';
import { useBrowserStore } from '@/stores/browserStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { usePlansStore } from '@/stores/plansStore';

/**
 * The scopes ZAC added to the Zoom app for a plan (a revert's: for the plan it reverts), and the way to take them back
 * off it once the plan is reverted: before, its revert still needs them. ZAC needs a signed-in Zoom session in its
 * browser for that: the panel opens when it has none.
 */
export function AddedScopes({ plan }: { plan: plans.PlanView }) {
  const remove = useConnectionStore((s) => s.removeAddedScopes);
  const loadPlan = usePlansStore((s) => s.loadPlan);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const live = plan.added_scopes?.live ?? [];
  const removed = plan.added_scopes?.removed ?? [];
  if (live.length === 0 && removed.length === 0) return null;
  // Offered once the plan is undone: a revert plan that finished, or a plan a revert undid; never while one runs.
  const reverted = (plan.origin === 'revert' && plan.status === 'completed') || plan.reverted_by_plan_id !== undefined || plan.status === 'reverted' || plan.status === 'revert_partial';

  const takeBack = async () => {
    setBusy(true);
    setNote(null);
    try {
      const res = await remove(plan.reverts_plan_id ?? plan.id);
      if (res.error === 'harness_session_required') useBrowserStore.getState().openPanel();
      if (res.error) setNote(res.message ?? res.error);
      else {
        const kept = res.kept.length === 0 ? '' : ` ${res.kept.length} stay: another plan still needs them.`;
        const failed = res.failed.length === 0 ? '' : ` ${res.failed.length} could not be cleared: ${res.failed.join(', ')}.`;
        setNote(`Took ${res.removed.length} scope${res.removed.length === 1 ? '' : 's'} off the app.${kept}${failed}`);
      }
      await loadPlan(plan.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-border bg-card p-4 text-sm">
      <div className="flex items-start gap-2">
        <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          {live.length > 0 ? (
            <>
              <p className="text-foreground">
                ZAC added {live.length} scope{live.length === 1 ? '' : 's'} to your Zoom app for this plan.{' '}
                {reverted ? 'Keep them for next time, or take them back off the app.' : 'Once the plan is reverted, you can take them back off the app.'}
              </p>
              <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                {live.map((scope) => {
                  const forStep = plan.steps.find((step) => { const added = step.run?.response?.['scopes_added']; return Array.isArray(added) && added.includes(scope); });
                  return <li key={scope}><span className="break-words font-mono">{scope}</span>{forStep && <> · added during the run for “{forStep.title}”</>}</li>;
                })}
              </ul>
            </>
          ) : (
            <p className="text-muted-foreground">The {removed.length} scope{removed.length === 1 ? '' : 's'} ZAC added for this plan {removed.length === 1 ? 'was' : 'were'} taken back off the app.</p>
          )}
          {note && <p className="mt-2 text-xs text-muted-foreground">{note}</p>}
        </div>
        {live.length > 0 && reverted && (
          <button onClick={() => void takeBack()} disabled={busy} className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs text-foreground hover:bg-secondary disabled:opacity-50">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Take them back off
          </button>
        )}
      </div>
    </section>
  );
}
